
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, User, Settings, Building, Upload } from "lucide-react";
import { useToast } from "./ui/use-toast";
import NetworkStatus from './NetworkStatus';
import { RoleGuard } from './RoleGuard';

export const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, user, profile, signOut, isAdministrator, isOrganizationUser } = useAuth();
  const { toast } = useToast();

  const handleSignOut = async () => {
    await signOut();
    // Navigation is handled in signOut function
  };

  const getFullName = () => {
    if (profile?.first_name || profile?.last_name) {
      return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    }
    return user?.email || "User";
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b bg-gradient-to-r from-sky-50 to-blue-50 backdrop-blur-sm">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-br from-sky-400 to-blue-500 rounded-full flex items-center justify-center">
                <img src="/NAC_Icon_Only.png" alt="NAC Logo" className="h-6 w-6" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-lg leading-tight text-[#0066FF]">National Airports Corporation</span>
                <span className="text-xs text-foreground leading-tight">Real-time Flight Information</span>
              </div>
            </Link>
            <nav className="hidden md:flex gap-6">
              <Link
                to="/flight-board"
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === "/flight-board" ? "text-primary" : "text-foreground"
                }`}
              >
                Flight Board
              </Link>
              
              <RoleGuard allowedRoles={['admin', 'administrator']}>
                <Link
                  to="/admin"
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    location.pathname.startsWith("/admin") ? "text-primary" : "text-foreground"
                  }`}
                >
                  Admin Dashboard
                </Link>
              </RoleGuard>

              <RoleGuard allowedRoles={['organization', 'organization_user']}>
                <Link
                  to="/organization"
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    location.pathname === "/organization" ? "text-primary" : "text-foreground"
                  }`}
                >
                  Organization Dashboard
                </Link>
              </RoleGuard>

              <RoleGuard allowedRoles={['admin', 'administrator', 'organization', 'organization_user']}>
                <Link
                  to="/UpdateFlights"
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    location.pathname === "/UpdateFlights" ? "text-primary" : "text-foreground"
                  }`}
                >
                  Update Flights
                </Link>
              </RoleGuard>
            </nav>
          </div>
          <div className="flex items-center gap-2">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <User className="h-4 w-4" />
                    <span className="hidden md:inline">{getFullName()}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>
                    <div>
                      <p>{getFullName()}</p>
                      <p className="text-xs text-foreground font-normal">
                        {profile?.role}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  
                  <RoleGuard allowedRoles={['admin', 'administrator']}>
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Admin Dashboard</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/admin/upload-schedule')}>
                      <Upload className="mr-2 h-4 w-4" />
                      <span>Upload Schedule</span>
                    </DropdownMenuItem>
                  </RoleGuard>

                  <RoleGuard allowedRoles={['organization', 'organization_user']}>
                    <DropdownMenuItem onClick={() => navigate('/organization')}>
                      <Building className="mr-2 h-4 w-4" />
                      <span>Organization Dashboard</span>
                    </DropdownMenuItem>
                  </RoleGuard>

                  <RoleGuard allowedRoles={['admin', 'administrator', 'organization', 'organization_user']}>
                    <DropdownMenuItem onClick={() => navigate('/UpdateFlights')}>
                      <img src="/NAC_Icon_Only.png" alt="NAC Logo" className="mr-2 h-4 w-4" />
                      <span>Update Flights</span>
                    </DropdownMenuItem>
                  </RoleGuard>
                  
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button onClick={() => navigate("/login")}>
                <User className="mr-2 h-4 w-4" />
                Login
              </Button>
            )}
          </div>
        </div>
      </header>
      <NetworkStatus />
    </>
  );
};
